import { parseLostDate } from './date.js';
import { redactSecrets } from './privacy.js';
import type { ItemCategory, ItemColor, LostDate, ReportPlace } from './report-schema.js';

export interface IntakeExtraction {
  redactedText: string;
  item: {
    description: string;
    category: ItemCategory;
    color: ItemColor;
    brand: string;
    distinguishingFeatures?: string;
  };
  lostDate: LostDate | null;
  dateInFuture: boolean;
  place: ReportPlace | null;
  walletPolicyRequired: boolean;
  confidence: number;
}

const CATEGORY_PATTERNS: ReadonlyArray<[ItemCategory, RegExp]> = [
  ['WALLET', /\b(wallet|purse)\b/iu],
  ['PHONE', /\b(smartphone|iphone|phone)\b/iu],
  ['KEYS', /\bkeys?\b/iu],
  ['BAG', /\b(bag|backpack|handbag)\b/iu],
  ['DOCUMENT', /\b(passport|document|id card|driver['’]?s license)\b/iu],
  ['JEWELLERY', /\b(ring|necklace|jewellery|jewelry|bracelet)\b/iu],
  ['ELECTRONICS', /\b(laptop|tablet|headphones|earbuds)\b/iu],
  ['CLOTHING', /\b(jacket|coat|hat|scarf)\b/iu],
];

const COLOR_PATTERNS: ReadonlyArray<[ItemColor, RegExp]> = [
  ['BLACK', /\bblack\b/iu],
  ['WHITE', /\bwhite\b/iu],
  ['GREY', /\b(grey|gray)\b/iu],
  ['BROWN', /\bbrown\b/iu],
  ['RED', /\bred\b/iu],
  ['ORANGE', /\borange\b/iu],
  ['YELLOW', /\byellow\b/iu],
  ['GREEN', /\bgreen\b/iu],
  ['BLUE', /\bblue\b/iu],
  ['PURPLE', /\bpurple\b/iu],
  ['PINK', /\bpink\b/iu],
];

function firstMatch<T extends string>(
  text: string,
  patterns: ReadonlyArray<readonly [T, RegExp]>,
  fallback: T,
): T {
  return patterns.find(([, pattern]) => pattern.test(text))?.[0] ?? fallback;
}

function parsePlace(text: string): ReportPlace | null {
  if (/don['’]?t know[^,.]*(?:where|the place)/iu.test(text)) return { kind: 'UNKNOWN' };

  const hotel =
    /(?:at|in)\s+(?:the\s+)?(?:hotel\s+)?([\p{Lu}][\p{L}\d '-]{1,100}?)(?=[,.]|\s+room\b|$)/u.exec(
      text,
    );
  if (hotel?.[1]) {
    const name = hotel[1]
      .trim()
      .replace(/\s+in\s+[\p{Lu}][\p{L}-]+$/u, '')
      .trim();
    return { kind: 'FREE_FORM', name, differentPlace: true };
  }

  const generic = /(?:\b(?:at|in)\b)\s+([^,.]{3,120})(?=[,.]|$)/iu.exec(text);
  if (generic?.[1]) return { kind: 'FREE_FORM', name: generic[1].trim(), differentPlace: true };
  return null;
}

export function parseDeterministicIntake(
  text: string,
  _language: 'en',
  now = new Date(),
): IntakeExtraction {
  const redactedText = redactSecrets(text.trim());
  const date = parseLostDate(text, now);
  const category = firstMatch(text, CATEGORY_PATTERNS, 'UNKNOWN');
  const color = firstMatch(text, COLOR_PATTERNS, 'UNKNOWN');
  const place = parsePlace(text);
  const walletPolicyRequired = /\b(wallet|purse|cash|card)\b/iu.test(text);
  const confidenceParts = [category !== 'UNKNOWN', date.value !== null, place !== null];
  const confidence = confidenceParts.filter(Boolean).length / confidenceParts.length;

  return {
    redactedText,
    item: {
      description: redactedText.slice(0, 1_000),
      category,
      color,
      brand: 'UNKNOWN',
    },
    lostDate: date.value,
    dateInFuture: date.future,
    place,
    walletPolicyRequired,
    confidence,
  };
}

export function mergeProviderIntake(
  deterministic: IntakeExtraction,
  provider: Record<string, unknown>,
  redactedInput: string,
  today: string,
): IntakeExtraction {
  const category = provider.category;
  const color = provider.color;
  const dateStart = provider.dateStart;
  const dateEnd = provider.dateEnd;
  const placeName = typeof provider.placeName === 'string' ? provider.placeName.trim() : '';
  const brand = typeof provider.brand === 'string' ? provider.brand.trim() : '';
  const validCategory = CATEGORY_VALUES.has(category as ItemCategory)
    ? (category as ItemCategory)
    : 'UNKNOWN';
  const validColor = COLOR_VALUES.has(color as ItemColor) ? (color as ItemColor) : 'UNKNOWN';
  const lostDate =
    deterministic.lostDate ??
    (provider.dateKind === 'UNKNOWN'
      ? { kind: 'UNKNOWN' as const }
      : provider.dateKind === 'KNOWN' &&
          isDay(dateStart) &&
          isDay(dateEnd) &&
          dateStart <= dateEnd &&
          dateEnd <= today
        ? { kind: 'KNOWN' as const, start: dateStart, end: dateEnd }
        : null);
  const place =
    deterministic.place ??
    (provider.placeKind === 'UNKNOWN'
      ? { kind: 'UNKNOWN' as const }
      : provider.placeKind === 'KNOWN' && placeName && isGrounded(placeName, redactedInput)
        ? {
            kind: 'FREE_FORM' as const,
            name: placeName,
            ...(typeof provider.placeContext === 'string' && provider.placeContext.trim()
              ? { context: provider.placeContext.trim() }
              : {}),
            differentPlace: true,
          }
        : null);
  const confidence =
    typeof provider.confidence === 'number' ? Math.min(1, Math.max(0, provider.confidence)) : 0;
  return {
    ...deterministic,
    item: {
      ...deterministic.item,
      category:
        deterministic.item.category === 'UNKNOWN' ? validCategory : deterministic.item.category,
      color: deterministic.item.color === 'UNKNOWN' ? validColor : deterministic.item.color,
      brand:
        deterministic.item.brand === 'UNKNOWN' && brand && isGrounded(brand, redactedInput)
          ? brand
          : deterministic.item.brand,
    },
    lostDate,
    dateInFuture: deterministic.dateInFuture || provider.dateInFuture === true,
    place,
    walletPolicyRequired:
      deterministic.walletPolicyRequired || provider.walletPolicyRequired === true,
    confidence: Math.max(deterministic.confidence, confidence),
  };
}

const CATEGORY_VALUES = new Set<ItemCategory>([
  'UNKNOWN',
  'WALLET',
  'PHONE',
  'KEYS',
  'BAG',
  'DOCUMENT',
  'JEWELLERY',
  'ELECTRONICS',
  'CLOTHING',
  'OTHER',
]);
const COLOR_VALUES = new Set<ItemColor>([
  'UNKNOWN',
  'BLACK',
  'WHITE',
  'GREY',
  'BROWN',
  'RED',
  'ORANGE',
  'YELLOW',
  'GREEN',
  'BLUE',
  'PURPLE',
  'PINK',
  'MULTICOLOR',
  'OTHER',
]);

function isDay(value: unknown): value is string {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function isGrounded(value: string, source: string): boolean {
  const normalize = (text: string) =>
    text
      .normalize('NFKD')
      .replace(/\p{M}/gu, '')
      .toLocaleLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .trim();
  return normalize(source).includes(normalize(value));
}
